import Tooltip from "@/v2-ui/tooltip"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { getValueTierConfig } from "@/v2-backoffice-client/customer/billing/customerBilling.constants"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"

export type CustomerValueBadgeProps = {
  customer: Pick<
    CustomerFragment,
    | "ValueTier"
    | "Billed12M"
    | "RevenueRank"
    | "RankedCustomerCount"
    | "PrimaryCurrency"
  >
}

// CustomerValueBadge surfaces where the customer's billed revenue ranks
// among all actively billed customers (server-derived ValueTier on the
// customer row, from lic.CustomerRevenueFacts). The tooltip carries the
// numbers behind the tier: rank N of M and the 12-month book. Renders
// nothing for customers with no invoice history.
function CustomerValueBadge(props: CustomerValueBadgeProps) {
  const { customer } = props

  const config = getValueTierConfig(customer.ValueTier)
  if (!config) return null

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <DotText variant={config.variant}>{config.label}</DotText>
      </Tooltip.Trigger>

      <Tooltip.Content side="top" className="ui-tooltip-content--panel">
        <div className="flex max-w-xs flex-col gap-1 p-3">
          <DotText
            variant={config.variant}
            className="flex font-medium text-popover-foreground"
          >
            {config.label}
            {customer.RevenueRank != null &&
              customer.RankedCustomerCount != null &&
              ` — #${customer.RevenueRank} of ${customer.RankedCustomerCount}`}
          </DotText>
          <p className="text-muted-foreground">{config.description}</p>

          <p className="mt-1 text-xs">
            <span className="text-muted-foreground">
              Billed last 12 months:{" "}
            </span>
            {formatInvoiceAmount(
              customer.Billed12M ?? 0,
              customer.PrimaryCurrency,
            )}
          </p>

          {config.fix && (
            <p className="mt-1 text-xs text-muted-foreground">{config.fix}</p>
          )}
        </div>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default CustomerValueBadge
